import React, { useState ,useEffect} from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Stack,
  Paper,
  IconButton,
  Divider,
  CircularProgress,
  TextField,
  useMediaQuery,
  useTheme
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import {
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  HourglassEmpty as HourglassIcon,
  OpenInNew as OpenInNewIcon,
  ContentCopy as ContentCopyIcon,
  Check as CheckIcon
} from '@mui/icons-material';
import { dateConverter } from '../../utils/dateConveter';
import Chip from '@mui/material/Chip';
import Grid from '@mui/material/Grid';


import { useSnackbar } from 'notistack';
const CompletePaymentModal = ({ open, data, onClose }) => {
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const getStatusChip = (status) => {
    const s = status && status.toLowerCase();
    if (s === 'completed')
      return <Chip icon={<CheckCircleIcon />} label="Success" size="small" color="success" sx={{ fontWeight: 600 }} />;
    if (s === 'pending')
      return <Chip icon={<HourglassIcon />} label="Pending" size="small" color="warning" sx={{ fontWeight: 600 }} />;
    if (s === 'failed')
      return <Chip icon={<CancelIcon />} label="Failed" size="small" color="error" sx={{ fontWeight: 600 }} />;
    return <Chip label={status} size="small" />;
  };
  
  const generateExplorerUrl = (chain, txId) => {
    if (!chain || !txId) return null;
    try {
      if (chain === 'ALGORAND') {
        return `${process.env.REACT_APP_PARAWALLET_PORTFOLIO}${txId}`;
      }
    } catch (error) {
      console.error('Error generating explorer URL:', error);
    }
    return null;
  };
  
  const [snack, setSnack] = React.useState({ open: false, message: "" });
  const showToast = (message) => setSnack({ open: true, message });
  const closeToast = () => setSnack({ open: false, message: "" });
  const [bridgeModalOpen, setBridgeModalOpen] = React.useState(false);

  function CopyButton({ text, label }) {
    const [copied, setCopied] = useState(false);
  
    const handleCopy = async () => {
      try {
        await navigator.clipboard.writeText(text);
        setCopied(true);
        enqueueSnackbar(`${label} copied to clipboard`, { variant: 'success' });
        setTimeout(() => setCopied(false), 1800);
      } catch (err) {
        enqueueSnackbar(`Failed to copy ${label}`, { variant: 'error' });
      }
    };

    return (
      <Button
        size="small"
        variant="text"
        onClick={handleCopy}
        startIcon={copied ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
      >
        {copied ? 'Copied' : 'Copy'}
      </Button>
    );
  }

  if (!open || !data) return null;

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth="md"
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            borderRadius: isMobile ? "6px" : "16px",
            width: { md: "720px" },
            maxWidth: { md: "720px" },
            ...(data?.status === "FAILED" || data?.status === "PENDING"
              ? { minHeight: "70vh" } 
              : {height:"70vh"}),
          },
        }}
      >
        {/* ---------------------- HEADER ---------------------- */}
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", pr: 6 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="h6">Transfer Details</Typography>
          </Box>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ p: isMobile ? 2 : 3 }}>
          <Box display="flex" flexDirection="column" gap={3}>

            {/* =======================================================
                1️⃣ TRANSFER SUMMARY SECTION 
            ======================================================= */}
            <Paper
              elevation={0}
              sx={{
                p: isMobile ? 2 : 3,
                bgcolor: "grey.50",
                borderRadius: 2,
                width: "100%",
              }}
            >
              <Box 
                sx={{ 
                  display: "flex", 
                  justifyContent: "space-between", 
                  alignItems: "center",
                  mb: 2 
                }}
              >
                <Typography
                  variant="h6"
                  sx={{ color: "primary.main", fontWeight: 600 }}
                >
                  Transfer Summary
                </Typography>

              </Box>


              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Stack spacing={2}>
                    {/* Asset Name */}
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Asset Name
                      </Typography>
                      <Typography variant="body1" fontWeight={500}>
                        {data?.assetName || "N/A"}
                      </Typography>
                    </Box>

                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      spacing={{ xs: 2, sm: 5 }}
                      useFlexGap
                      flexWrap="wrap"
                    >
                      <Box sx={{ display: "flex", flexDirection: "column" }}>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                          Project Name
                        </Typography>
                        <Typography variant="body1" fontWeight={500}>
                          {data?.projectName || "N/A"}
                        </Typography>
                      </Box>

                      <Box sx={{ display: "flex", flexDirection: "column" }}>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                          Tokens
                        </Typography>
                        <Typography variant="body1" fontWeight={500}>
                          {data?.numberOfToken} {data?.tokenName || "FRAX"}
                        </Typography>
                      </Box>

                      <Box sx={{ display: "flex", flexDirection: "column" }}>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                          Amount
                        </Typography>
                        <Typography variant="body1" fontWeight={500}>
                          {data?.amount} 
                        </Typography>
                      </Box>

                      
                    </Stack>

                    

                  </Stack>
                </Grid>
              </Grid>
            </Paper>

            {/* =======================================================
                2️⃣ RECEIVER INFORMATION
            ======================================================= */}
            <Paper
              elevation={0}
              sx={{ p: isMobile ? 2 : 3, bgcolor: "grey.50", borderRadius: 2 }}
            >
              <Typography
                variant="h6"
                sx={{ mb: 2, color: "primary.main", fontWeight: 600 }}
              >
                Information
              </Typography>

              <Stack spacing={2}>
                                {/* Legal Name */}
                                <Box sx={{ display: "flex", justifyContent: "space-between", flexDirection: { xs: "column", sm: "row" }, gap: { xs: 0.5, sm: 2 } }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Sender Legal Name:
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {data?.receiverLegalName || data?.legalName || '-'}
                  </Typography>
                </Box>

                {/* Wallet Name */}
                <Box sx={{ display: "flex", justifyContent: "space-between", flexDirection: { xs: "column", sm: "row" }, gap: { xs: 0.5, sm: 2 } }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Receiver DLT Wallet Name:
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {data?.receiverDltWalletname || "-"}
                  </Typography>
                </Box>
              </Stack>
            </Paper>

            {/* =======================================================
                3️⃣ STATUS & ERROR (IF FAILED)
            ======================================================= */}
            <Paper elevation={0} sx={{ p: 2, borderRadius: 2, bgcolor: "grey.50" }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">
                    Status
                  </Typography>
                  <Box sx={{ mt: 1 }}>{getStatusChip(data?.status)}</Box>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">
                    Date
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {dateConverter({value:data?.created_at,momentIsTrue:true, dateFormat:"DD/MM/YYYY"})}
                  </Typography>
                </Grid>
              </Grid>

            </Paper>

            {/* =======================================================
                4️⃣ BLOCKCHAIN HASHES (Sender + Receiver)
            ======================================================= */}
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                Transaction Hashes
              </Typography>

              <Box display="flex" flexDirection="column" gap={2}>

                {/* Sender TX */}
                <Box>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems={{ xs: "flex-start", sm: "center" }}
                    flexDirection={{ xs: "column", sm: "row" }}
                    gap={{ xs: 1, sm: 2 }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        Sender ({data?.sender?.blockChainNetwork})
                    </Typography>

                    {data.sender?.dltAccount ? (
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<OpenInNewIcon />}
                        fullWidth={isMobile}
                        onClick={() => {
                          const url = generateExplorerUrl(data?.sender?.blockChainNetwork, data?.sender?.dltAccount);
                          if (url) window.open(url, '_blank');
                        }}
                      >
                        {data?.sender?.blockChainNetwork === "POLYGON"
                        ? "View on PolygonScan"
                        : data?.sender?.blockChainNetwork === "ALGORAND"
                        ? "View on Pera Explorer"
                        : "View on Explorer"}
                      </Button>
                    ) : (
                      <Button size="small" disabled variant="outlined">
                        View on Explorer
                      </Button>
                    )}
                  </Box>

                  <Box
                    sx={{
                      mt: 1,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      bgcolor: "grey.50",
                      p: 1.5,
                      borderRadius: 1,
                      flexDirection: { xs: "column", sm: "row" },
                      alignItems: { xs: "stretch", sm: "center" },
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: "monospace",
                        flex: 1,
                        overflowWrap: "anywhere",
                        wordBreak: "break-word",
                      }}
                    >
                      {data.sender?.dltAccount || "-"}
                    </Typography>
                      {data.sender?.dltAccount && (
                      <CopyButton text={data?.sender?.dltAccount} label="Sender DLT Account" />
                    )}
                  </Box>
                </Box>

                {/* Receiver TX */}
                <Box>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems={{ xs: "flex-start", sm: "center" }}
                    flexDirection={{ xs: "column", sm: "row" }}
                    gap={{ xs: 1, sm: 2 }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      Receiver ({data?.receiver?.blockChainNetwork})
                    </Typography>

                    {data.receiver?.dltAccount ? (
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<OpenInNewIcon />}
                        fullWidth={isMobile}
                        onClick={() => {
                          const url = generateExplorerUrl(data?.receiver?.blockChainNetwork, data?.receiver?.dltAccount);
                          if (url) window.open(url, '_blank');
                        }}
                      >
                        {data?.receiver?.blockChainNetwork === "POLYGON"
                        ? "View on Polygonscan"
                        : data?.receiver?.blockChainNetwork === "ALGORAND"
                        ? "View on Pera Explorer"
                        : "View on Explorer"}
                      </Button>
                    ) : (
                      <Button size="small" disabled variant="outlined">
                        View on Explorer
                      </Button>
                    )}
                  </Box>

                  <Box
                    sx={{
                      mt: 1,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      bgcolor: "grey.50",
                      p: 1.5,
                      borderRadius: 1,
                      flexDirection: { xs: "column", sm: "row" },
                      alignItems: { xs: "stretch", sm: "center" },
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: "monospace",
                        flex: 1,
                        overflowWrap: "anywhere",
                        wordBreak: "break-word",
                      }}
                    >
                      {data.receiver?.dltAccount || "-"}
                    </Typography>

                    {data.receiver?.dltAccount && (
                      <CopyButton text={data?.receiver?.dltAccount} label="Receiver DLT Account" />
                    )}
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

    </>
  );
};

export default CompletePaymentModal;
